import { useEffect, useRef, useState } from "react";
import { api, fileUrl, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { Microphone, PaperPlaneTilt, Stop } from "@phosphor-icons/react";

export default function PortalMessages() {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [recording, setRecording] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const load = () => api.get("/portal/messages").then(({ data }) => setMessages(data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const sendText = async () => {
    if (!text.trim()) return;
    try {
      const { data } = await api.post("/portal/messages", { type: "text", content: text });
      setMessages((m) => [...m, data]);
      setText("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to send");
    }
  };

  const startRecording = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const recorder = new MediaRecorder(stream);
    chunksRef.current = [];
    recorder.ondataavailable = (e) => chunksRef.current.push(e.data);
    recorder.onstop = async () => {
      const blob = new Blob(chunksRef.current, { type: "audio/webm" });
      const formData = new FormData();
      formData.append("file", blob, "voice-note.webm");
      try {
        const { data: upload } = await api.post("/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
        const { data } = await api.post("/portal/messages", { type: "audio", file_id: upload.file_id });
        setMessages((m) => [...m, data]);
      } catch {
        toast.error("Failed to send audio clip");
      }
    };
    recorder.start();
    mediaRecorderRef.current = recorder;
    setRecording(true);
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setRecording(false);
  };

  return (
    <div data-testid="portal-messages-page" className="flex flex-col h-[65vh] bg-[#141414] border border-[#27272A] rounded-2xl">
      <div className="flex-1 overflow-y-auto p-5 space-y-3">
        {messages.map((m) => (
          <div
            key={m.id}
            data-testid={`portal-message-bubble-${m.id}`}
            className={`max-w-md px-4 py-2.5 rounded-2xl text-sm ${
              m.sender === "client" ? "bg-[#D4AF37] text-black ml-auto" : "bg-[#0A0A0A] text-white border border-[#27272A]"
            }`}
          >
            {m.type === "text" ? m.content : <audio controls src={fileUrl(m.file_id)} className="w-56" />}
          </div>
        ))}
        {!messages.length && <p className="text-sm text-[#71717A]">Message Coach Conde anytime.</p>}
      </div>
      <div className="border-t border-[#27272A] p-4 flex items-center gap-2">
        <input
          data-testid="portal-message-text-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && sendText()}
          placeholder="Message your coach..."
          className="flex-1 bg-[#0A0A0A] border border-[#27272A] rounded-full px-4 py-2.5 text-white text-sm"
        />
        <button
          data-testid="portal-message-record-button"
          onClick={recording ? stopRecording : startRecording}
          className={`p-2.5 rounded-full transition-colors ${recording ? "bg-red-500 text-white" : "bg-white/10 text-white"}`}
        >
          {recording ? <Stop size={18} /> : <Microphone size={18} />}
        </button>
        <button data-testid="portal-message-send-button" onClick={sendText} className="p-2.5 rounded-full bg-[#D4AF37] text-black">
          <PaperPlaneTilt size={18} />
        </button>
      </div>
    </div>
  );
}
